import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the structure for AddressDetails
interface AddressDetails {
  isPermanentNotSameAsCurrentFlag?: Record<string, any>;
  currentAddress?: any[];
  permanentAddress?: any[];
  [key: string]: any;
}

interface ApplicationLoanDetailsData {
  studentDetails: any[];
  addressDetails: AddressDetails;
  academicDetails: any[];
  employmentDetails: any[];
  companyAddressDetails: any[];
  entranceDetails?: any;
  loanDetails?: any;
  expenseDetails: any[];
  referenceDetails: any[];
  coApplicantDetails: any[];
  coApplicantAddressDetails: Record<string, AddressDetails>;
  coApplicantEmploymentDetails: Record<string, any>;
  coApplicantCompletionRate: string;
  collateralDetails: any[];
  partnerDetails: {};
  educationGap: number;
  coApplicantBasicDetails: Record<string, any[]>;
}

const initialState: ApplicationLoanDetailsData = {
  studentDetails: [],
  addressDetails: {},
  employmentDetails: [],
  companyAddressDetails: [],
  academicDetails: [],
  entranceDetails: [],
  loanDetails: [],
  expenseDetails: [],
  referenceDetails: [],
  coApplicantDetails: [],
  coApplicantAddressDetails: {},
  coApplicantEmploymentDetails: {},
  coApplicantCompletionRate: "",
  collateralDetails: [],
  partnerDetails: {},
  educationGap: 0,
  coApplicantBasicDetails: {},
};

const applicationLoanDetailsDataSlice = createSlice({
  name: "applicationLoanDetailsData",
  initialState,
  reducers: {
    setApplicantLoanDetails: (
      state,
      action: PayloadAction<
        { StudentDetails?: any[]; AddressDetails?: AddressDetails } | undefined
      >
    ) => {
      state.studentDetails = action.payload?.StudentDetails || [];
      state.addressDetails = action.payload?.AddressDetails || {};
    },
    setAcademicDetails: (
      state,
      action: PayloadAction<{ academicDetails?: any[] } | undefined>
    ) => {
      // Flatten academicDetails so each course's details are merged into a single array
      const raw = action.payload?.academicDetails || [];
      const flattened: any[] = [];
      raw.forEach((courseObj: any) => {
        Object.values(courseObj).forEach((courseArr: any) => {
          if (Array.isArray(courseArr)) {
            flattened.push(...courseArr);
          }
        });
      });
      state.academicDetails = flattened;
    },
    setEmploymentDetails: (state, action: PayloadAction<any>) => {
      state.employmentDetails = action.payload?.EmploymentDetails;
      state.companyAddressDetails = action.payload?.CompanyAddressDetails;
    },
    setEntranceDetails: (state, action: PayloadAction<any>) => {
      state.entranceDetails = action.payload;
    },
    setLoanDetails: (state, action: PayloadAction<any>) => {
      state.loanDetails = action.payload;
    },
    setExpenseDetails: (state, action: PayloadAction<any>) => {
      state.expenseDetails = action.payload;
    },
    setReferenceDetails: (state, action: PayloadAction<any>) => {
      state.referenceDetails = action.payload;
    },
    setCoApplicantDetails: (state, action: PayloadAction<any>) => {
      state.coApplicantDetails = action.payload;
    },
    setCoApplicantAddressDetails: (
      state,
      action: PayloadAction<{
        coApplicantId: string | number;
        addressDetails: AddressDetails;
      }>
    ) => {
      const { coApplicantId, addressDetails } = action.payload;
      state.coApplicantAddressDetails = {
        ...state.coApplicantAddressDetails,
        [coApplicantId]: addressDetails,
      };
    },
    setCoApplicantEmploymentDetails: (
      state,
      action: PayloadAction<{
        coApplicantId: string | number;
        employmentDetails: any;
      }>
    ) => {
      const { coApplicantId, employmentDetails } = action.payload;
      state.coApplicantEmploymentDetails = {
        ...state.coApplicantEmploymentDetails,
        [coApplicantId]: employmentDetails,
      };
    },
    setCoApplicantCompletionRate: (state, action) => {
      state.coApplicantCompletionRate = action.payload;
    },
    setCollateralDetails: (state, action: PayloadAction<any>) => {
      state.collateralDetails = action.payload;
    },
    setPartnerDetails: (state, action) => {
      state.partnerDetails = action.payload;
    },
    setEducationGap: (state, action) => {
      state.educationGap = action.payload;
    },

    // Clear all state to initial values
    clearAllApplicantLoanDetails: (state) => {
      Object.assign(state, initialState);
    },

    setCoApplicantBasicDetails: (
      state,
      action: PayloadAction<{
        coApplicantId: string | number;
        basicDetails: any[];
      }>,
    ) => {
      const { coApplicantId, basicDetails } = action.payload;
      state.coApplicantBasicDetails = {
        ...state.coApplicantBasicDetails,
        [coApplicantId]: Array.isArray(basicDetails) ? basicDetails : [],
      };
    },
  },
});

export const {
  setApplicantLoanDetails,
  setAcademicDetails,
  setEmploymentDetails,
  setEntranceDetails,
  setLoanDetails,
  setExpenseDetails,
  setReferenceDetails,
  setCoApplicantDetails,
  setCoApplicantAddressDetails,
  setCoApplicantEmploymentDetails,
  setCoApplicantCompletionRate,
  clearAllApplicantLoanDetails,
  setCollateralDetails,
  setPartnerDetails,
  setEducationGap,
  setCoApplicantBasicDetails,
} = applicationLoanDetailsDataSlice.actions;
export default applicationLoanDetailsDataSlice.reducer;